import React, { useState, useEffect } from "react";
import { Container, Button, Table, Modal, Form, Alert } from "react-bootstrap";
import Select, { MultiValue, OptionProps } from 'react-select';
import { useNavigate } from "react-router-dom";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import Sidebar from "./Sidebar";

type SelectOption = {
    value: number | 'select-all';
    label: string;
    data?: Brand;
};

type CustomOptionProps = OptionProps<SelectOption, true> & {
    data: SelectOption;
};

interface Customer {
    id: number;
    username: string;
    password: string;
    role: string;
    customer_slug: string;
    brand_id?: number;
    brands?: any;
    brand_name?: string;
}

// Brand type
interface Brand {
    id: number;
    name: string;
    autologin_url: string;
}

const API_BASE = "https://api.performancedashboard.io/api/metricool";

const AddCustomerPage: React.FC = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("authToken");

    // State hooks
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
    const [viewingCustomer] = useState<Customer | null>(null);

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("customer");
    const [selectedBrandIds, setSelectedBrandIds] = useState<number[]>([]);
    const [selectedBrands, setSelectedBrands] = useState<Array<{ id: number; name: string; autologin_url: string }>>([]);

    const fetchCustomers = async () => {
        try {
            const response = await fetch(`${API_BASE}/customers`, {
                method: "GET",
                credentials: "include",
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Error fetching customers: ${response.status} - ${text.substring(0, 200)}`);
            }

            const data: Customer[] = await response.json();
            setCustomers(data);
        } catch (err: any) {
            setError(err.message);
            console.error("Error fetching customers:", err);
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await fetch(`${API_BASE}/brands`, {
                method: "GET",
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("Failed to fetch brands");
            }

            const data: Brand[] = await response.json();
            setBrands(data);
        } catch (err: any) {
            setError(err.message);
            console.error("Error fetching brands:", err);
        }
    };



    useEffect(() => {
        if (!token) return;

        const fetchData = async () => {
            setLoading(true);
            await Promise.all([fetchCustomers(), fetchBrands()]);
            setLoading(false);
        };
        fetchData();
    }, [token]);

    if (!token) {
        navigate("/");
        return null;
    }

    const EditIcon = FiEdit as React.ComponentType<{ size?: number }>;
    const DeleteIcon = FiTrash2 as React.ComponentType<{ size?: number }>;

    const handleLogout = () => {
        localStorage.clear();
        navigate("/");
    };

    const handleAddCustomerClick = () => {
        resetForm();
        setShowModal(true);
    };

    const handleEdit = (customer: Customer) => {
        setEditingCustomer(customer);
        setUsername(customer.username);
        setPassword(customer.password);
        setRole(customer.role);

        // Parse brand_id if it's a string (from API) or use the array directly
        let brandIds: number[] = [];
        if (customer.brand_id) {
            if (typeof customer.brand_id === 'string') {
                try {
                    brandIds = JSON.parse(customer.brand_id);
                } catch (e) {
                    console.error('Error parsing brand_id:', e);
                    // If parsing fails, try to convert to number array directly
                    brandIds = [Number(customer.brand_id)].filter(Boolean);
                }
            } else if (Array.isArray(customer.brand_id)) {
                brandIds = customer.brand_id;
            }
        }

        // Set selected brand IDs and find corresponding brand objects
        setSelectedBrandIds(brandIds);
        const selectedBrands = brands.filter(brand => brandIds.includes(brand.id));
        setSelectedBrands(selectedBrands);

        setShowModal(true);
    };

    console.log(selectedBrandIds)

    const handleDelete = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this customer?")) return;

        try {
            setError(null);

            const response = await fetch(`${API_BASE}/customers/${id}`, {
                method: "DELETE",
                credentials: "include",
            });

            if (!response.ok) {
                const responseText = await response.text();
                throw new Error(`Failed to delete customer: ${response.status} - ${responseText.substring(0, 200)}`);
            }

            await fetchCustomers();
        } catch (err: any) {
            setError(err.message);
            console.error("Error deleting customer:", err);
        }
    };


    const resetForm = () => {
        setUsername("");
        setPassword("");
        setRole("customer");
        setSelectedBrandIds([]);
        setSelectedBrands([]);
        setEditingCustomer(null);
        setError(null);
    };

    const handleSave = async () => {
        if (!username || !password || selectedBrandIds.length === 0) {
            setError("All fields are required, including at least one brand.");
            return;
        }

        try {
            setError(null);

            const customerData = {
                username,
                password,
                role,
                brand_id: JSON.stringify(selectedBrandIds),
            };

            let response: Response;

            if (editingCustomer) {
                response = await fetch(`${API_BASE}/customers/${editingCustomer.id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(customerData),
                    credentials: "include",
                });
            } else {
                response = await fetch(`${API_BASE}/customers`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(customerData),
                    credentials: "include",
                });
            }

            if (!response.ok) {
                const responseText = await response.text();
                let errorData;
                try {
                    errorData = JSON.parse(responseText);
                } catch {
                    errorData = { message: responseText.substring(0, 200) };
                }
                throw new Error(errorData.message || `Failed to save customer: ${response.status}`);
            }

            // Reset form and close modal
            resetForm();
            setShowModal(false);
            fetchCustomers();
            fetchBrands()
        } catch (err: any) {
            setError(err.message);
            console.error("Error saving customer:", err);
        }
    };

    if (loading) {
        return (
            <Container fluid style={{ height: "100vh", display: "flex", padding: 0 }}>
                <div style={{ flex: 1, padding: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    Loading...
                </div>
            </Container>
        );
    }

    return (
        <Container fluid style={{ height: "100vh", display: "flex", padding: 0 }}>
            <Sidebar onLogout={handleLogout} />
            <div style={{ flex: 1, padding: "20px", overflowY: "auto" }}>
                {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <h2>Customer Management</h2>
                    <div style={{ display: "flex", gap: "10px" }}>
                        <Button variant="primary" onClick={handleAddCustomerClick}>
                            Add Customer
                        </Button>
                    </div>
                </div>

                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Username / Email</th>
                            <th>Password</th>
                            <th>Brand</th>
                            <th>Role</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {customers.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center">No customers found. Add one to get started!</td>
                            </tr>
                        ) : (
                            customers.map((customer) => (
                                <tr key={customer.id}>
                                    <td>{customer.id}</td>
                                    <td>{customer.username}</td>
                                    <td>{customer.password}</td>

                                    <td width="400px">
                                        {customer?.brands?.map((brand: any) => brand.name).join(", ")}

                                    </td>
                                    <td>{customer.role}</td>
                                    <td>
                                        <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(customer)} style={{ marginRight: "5px" }}>
                                            <EditIcon size={16} />
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(customer.id)}>
                                            <DeleteIcon size={16} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <Modal show={showModal} onHide={() => setShowModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editingCustomer ? "Edit Customer" : "Add Customer"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group controlId="username" className="mb-2">
                                <Form.Label>Username / Email</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter username/email"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                            </Form.Group>

                            <Form.Group controlId="password" className="mb-2">
                                <Form.Label>Password</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </Form.Group>

                            <Form.Group controlId="brand" className="mb-2">
                                <Form.Label>Brand</Form.Label>
                                <div className="mb-3">
                                    <Select
                                        isMulti
                                        options={[
                                            { value: 'select-all' as const, label: 'Select All' },
                                            ...brands.map(brand => ({
                                                value: brand.id,
                                                label: brand.name,
                                                data: brand
                                            }))
                                        ]}
                                        value={selectedBrands.map(brand => ({
                                            value: brand.id,
                                            label: brand.name,
                                            data: brand
                                        }))}
                                        onChange={(selected: MultiValue<SelectOption>) => {
                                            if (!selected) {
                                                setSelectedBrands([]);
                                                setSelectedBrandIds([]);
                                                return;
                                            }

                                            // Handle "Select All"
                                            if (selected.some(option => option.value === 'select-all')) {
                                                setSelectedBrands([...brands]);
                                                setSelectedBrandIds(brands.map(b => b.id));
                                                return;
                                            }

                                            // Handle regular selection
                                            const selectedBrands = selected
                                                .filter((option): option is { value: number; label: string; data: Brand } =>
                                                    typeof option.value === 'number' && option.data !== undefined
                                                )
                                                .map(option => option.data);

                                            setSelectedBrands(selectedBrands);
                                            setSelectedBrandIds(selectedBrands.map(brand => brand.id));
                                        }}
                                        placeholder="Search and select brands..."
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{
                                            Option: (props: CustomOptionProps) => {
                                                const { data, isSelected, innerRef, innerProps } = props;
                                                const label = props.children;

                                                if (data.value === 'select-all') {
                                                    return (
                                                        <div ref={innerRef} {...innerProps} className="px-3 py-2">
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={selectedBrands.length === brands.length}
                                                                    onChange={() => { }}
                                                                    aria-label="Select all brands"
                                                                />
                                                                <label className="form-check-label fw-bold">
                                                                    {selectedBrands.length === brands.length ? 'Deselect All' : 'Select All'}
                                                                </label>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div ref={innerRef} {...innerProps} className="px-3 py-1">
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={isSelected}
                                                                onChange={() => { }}
                                                                aria-label={`Select ${label}`}
                                                            />
                                                            <label className="form-check-label">
                                                                <div><strong>{label}</strong></div>
                                                                {data.data?.autologin_url && (
                                                                    <div><small className="text-muted">{data.data.autologin_url}</small></div>
                                                                )}
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        }}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '42px',
                                                borderColor: '#ced4da',
                                                '&:hover': {
                                                    borderColor: '#86b7fe',
                                                    boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                                                },
                                                '&:focus-within': {
                                                    borderColor: '#86b7fe',
                                                    boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                                                }
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 5,
                                                border: '1px solid #dee2e6',
                                                boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
                                            }),
                                            multiValue: (base) => ({
                                                ...base,
                                                backgroundColor: '#e9ecef',
                                                borderRadius: '4px',
                                                padding: '0 0.5rem',
                                                margin: '0.2rem',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }),
                                            multiValueLabel: (base) => ({
                                                ...base,
                                                color: '#212529',
                                                fontSize: '0.875rem',
                                                padding: 0
                                            }),
                                            multiValueRemove: (base) => ({
                                                ...base,
                                                color: '#6c757d',
                                                marginLeft: '0.5rem',
                                                ':hover': {
                                                    backgroundColor: 'transparent',
                                                    color: '#dc3545'
                                                }
                                            })
                                        }}
                                    />
                                </div>
                            </Form.Group>



                            <Form.Group controlId="role" className="mb-2">
                                <Form.Label>Role</Form.Label>
                                <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
                                    <option value="customer">Customer</option>
                                    <option value="super_admin">Admin</option>
                                </Form.Select>
                            </Form.Group>

                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleSave} disabled={loading}>
                            {editingCustomer ? "Save Changes" : "Add Customer"}
                        </Button>
                    </Modal.Footer>
                </Modal >

                <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Customer Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {viewingCustomer && (
                            <dl className="row mb-0">
                                <dt className="col-sm-4">ID</dt>
                                <dd className="col-sm-8">{viewingCustomer.id}</dd>

                                <dt className="col-sm-4">Username / Email</dt>
                                <dd className="col-sm-8">{viewingCustomer.username}</dd>

                                <dt className="col-sm-4">Password</dt>
                                <dd className="col-sm-8">{viewingCustomer.password}</dd>

                                <dt className="col-sm-4">Brand</dt>
                                <dd className="col-sm-8">{viewingCustomer.brand_name || 'N/A'}</dd>


                                <dt className="col-sm-4">Role</dt>
                                <dd className="col-sm-8">{viewingCustomer.role}</dd>

                            </dl>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </div >
        </Container >
    );
};

export default AddCustomerPage;